import React, { useState } from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiInstagram, FiHeart, FiMessageCircle, FiShare, FiExternalLink } = FiIcons;

const InstagramPosts = () => {
  const [selectedCategory, setSelectedCategory] = useState('all');

  const fadeInUp = {
    initial: { opacity: 0, y: 30 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.6 }
  };

  const staggerContainer = {
    animate: {
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  // Mock Instagram posts data - In real implementation, you'd fetch from Instagram API
  const instagramPosts = [
    {
      id: 1,
      category: 'transformation',
      image: 'https://images.unsplash.com/photo-1571019613454-1cb2f99b2d8b?ixlib=rb-4.0.3&auto=format&fit=crop&w=600&q=80',
      caption: 'Amazing 90-day transformation! Sarah lost 15kg and gained confidence. Her secret? Personalized nutrition + consistent habits. 💪 #TransformationTuesday',
      likes: 234,
      comments: 18,
      date: '2 days ago'
    },
    {
      id: 2,
      category: 'nutrition',
      image: 'https://images.unsplash.com/photo-1490645935967-10de6ba17061?ixlib=rb-4.0.3&auto=format&fit=crop&w=600&q=80',
      caption: 'Power-packed breakfast for busy professionals! 🥗 High protein, healthy fats, complex carbs. Recipe in comments! #HealthyEating #NutritionTips',
      likes: 156,
      comments: 12,
      date: '3 days ago'
    },
    {
      id: 3,
      category: 'tips',
      image: 'https://images.unsplash.com/photo-1559757148-5c350d0d3c56?ixlib=rb-4.0.3&auto=format&fit=crop&w=600&q=80',
      caption: '5 Simple habits that changed everything for our clients:\n1. Hydrate first thing\n2. Protein with every meal\n3. Walk after meals\n4. Sleep 7-8 hours\n5. Plan meals ahead\n\nSmall changes, BIG results! 🌟',
      likes: 189,
      comments: 25,
      date: '5 days ago'
    },
    {
      id: 4,
      category: 'recipes',
      image: 'https://images.unsplash.com/photo-1546549032-9571cd6b27df?ixlib=rb-4.0.3&auto=format&fit=crop&w=600&q=80',
      caption: 'Indian-style quinoa bowl that our clients LOVE! 🍛 Packed with protein, fiber, and flavor. Perfect for weight loss goals. Swipe for recipe! #HealthyIndianFood',
      likes: 201,
      comments: 31,
      date: '1 week ago'
    },
    {
      id: 5,
      category: 'motivation',
      image: 'https://images.unsplash.com/photo-1571019614242-c5c5dee9f50b?ixlib=rb-4.0.3&auto=format&fit=crop&w=600&q=80',
      caption: '"I thought I needed to eat less. Turns out I needed to eat BETTER." - Priya, lost 12kg in 4 months 💚 Your body needs fuel, not restriction! #MindsetShift',
      likes: 167,
      comments: 14,
      date: '1 week ago'
    },
    {
      id: 6,
      category: 'transformation',
      image: 'https://images.unsplash.com/photo-1559757175-0eb30cd8c063?ixlib=rb-4.0.3&auto=format&fit=crop&w=600&q=80',
      caption: 'PCOD warrior Anjali\'s incredible journey! 6 months of personalized nutrition helped regulate her hormones AND lose 18kg. Proof that with right guidance, anything is possible! 👑 #PCODWarrior',
      likes: 298,
      comments: 42,
      date: '2 weeks ago'
    },
    {
      id: 7,
      category: 'tips',
      image: 'https://images.unsplash.com/photo-1512621776951-a57141f2eefd?ixlib=rb-4.0.3&auto=format&fit=crop&w=600&q=80',
      caption: 'Meal prep Sunday done right! 🥘 Our clients prep these 5 components:\n✅ Protein (chicken, paneer, dal)\n✅ Carbs (rice, quinoa, roti)\n✅ Veggies (roasted mix)\n✅ Healthy fats (nuts, seeds)\n✅ Snacks (fruits, yogurt)\n\nSaves 5+ hours during the week! ⏰',
      likes: 145,
      comments: 19,
      date: '2 weeks ago'
    },
    {
      id: 8,
      category: 'nutrition',
      image: 'https://images.unsplash.com/photo-1498837167922-ddd27525d352?ixlib=rb-4.0.3&auto=format&fit=crop&w=600&q=80',
      caption: 'Myth Buster Monday! 🚫 "Eating after 7 PM causes weight gain"\n\nTRUTH: Your body doesn\'t have a clock! What matters is:\n• Total calories\n• Food quality\n• Portion size\n• Activity level\n\nStop watching the clock, start listening to your body! 🕐',
      likes: 178,
      comments: 23,
      date: '3 weeks ago'
    }
  ];

  const categories = [
    { id: 'all', name: 'All Posts', count: instagramPosts.length },
    { id: 'transformation', name: 'Transformations', count: instagramPosts.filter(p => p.category === 'transformation').length },
    { id: 'nutrition', name: 'Nutrition', count: instagramPosts.filter(p => p.category === 'nutrition').length },
    { id: 'tips', name: 'Tips', count: instagramPosts.filter(p => p.category === 'tips').length },
    { id: 'recipes', name: 'Recipes', count: instagramPosts.filter(p => p.category === 'recipes').length },
    { id: 'motivation', name: 'Motivation', count: instagramPosts.filter(p => p.category === 'motivation').length }
  ];

  const filteredPosts = selectedCategory === 'all' 
    ? instagramPosts 
    : instagramPosts.filter(post => post.category === selectedCategory);

  return (
    <div className="pt-16">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-[#F9FFF5] to-[#EAF8EC] py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div 
            {...fadeInUp}
            className="text-center max-w-4xl mx-auto"
          >
            <div className="inline-block mb-6">
              <span className="bg-[#2E7D32] text-white px-4 py-2 rounded-full text-sm font-medium">
                Social Gallery
              </span>
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-[#0B1320] font-poppins mb-6">
              Success Stories & 
              <span className="text-[#2E7D32]"> Daily Inspiration</span>
            </h1>
            <p className="text-xl text-[#4A4A4A] leading-relaxed mb-8">
              Follow our journey on Instagram for daily nutrition tips, transformation stories, 
              healthy recipes, and motivation from our community of wellness warriors.
            </p>
            
            <a 
              href="https://www.instagram.com/samsnutrihub" 
              target="_blank" 
              rel="noopener noreferrer"
              className="inline-flex items-center space-x-2 bg-gradient-to-r from-purple-500 to-pink-500 text-white px-8 py-4 rounded-lg font-semibold hover:from-purple-600 hover:to-pink-600 transition-colors duration-200"
            >
              <SafeIcon icon={FiInstagram} className="h-5 w-5" />
              <span>Follow @samsnutrihub</span>
              <SafeIcon icon={FiExternalLink} className="h-4 w-4" />
            </a>
          </motion.div>
        </div>
      </section>

      {/* Filter Section */}
      <section className="bg-white py-12 sticky top-16 z-40 border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div 
            {...fadeInUp}
            className="flex flex-wrap justify-center gap-4"
          >
            {categories.map((category) => (
              <button
                key={category.id}
                onClick={() => setSelectedCategory(category.id)}
                className={`px-6 py-3 rounded-full font-medium transition-colors duration-200 ${
                  selectedCategory === category.id
                    ? 'bg-[#2E7D32] text-white'
                    : 'bg-gray-100 text-[#4A4A4A] hover:bg-gray-200'
                }`}
              >
                {category.name} ({category.count})
              </button>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Posts Grid */}
      <section className="bg-[#F9FFF5] py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div 
            key={selectedCategory}
            variants={staggerContainer}
            initial="initial"
            animate="animate"
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8"
          >
            {filteredPosts.map((post, index) => (
              <motion.div 
                key={post.id}
                variants={fadeInUp}
                className="bg-white rounded-2xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow duration-300 group"
              >
                {/* Image */}
                <div className="relative overflow-hidden">
                  <img 
                    src={post.image} 
                    alt={`Instagram post ${post.id}`}
                    className="w-full h-64 object-cover group-hover:scale-105 transition-transform duration-300"
                  />
                  <div className="absolute top-4 right-4">
                    <div className="bg-black/20 backdrop-blur-sm rounded-full p-2">
                      <SafeIcon icon={FiInstagram} className="h-5 w-5 text-white" />
                    </div>
                  </div>
                </div>

                {/* Content */}
                <div className="p-6">
                  <div className="mb-4">
                    <span className={`inline-block px-3 py-1 rounded-full text-xs font-medium ${
                      post.category === 'transformation' ? 'bg-green-100 text-green-800' :
                      post.category === 'nutrition' ? 'bg-blue-100 text-blue-800' :
                      post.category === 'tips' ? 'bg-yellow-100 text-yellow-800' :
                      post.category === 'recipes' ? 'bg-purple-100 text-purple-800' :
                      'bg-pink-100 text-pink-800'
                    }`}>
                      {post.category.charAt(0).toUpperCase() + post.category.slice(1)}
                    </span>
                  </div>

                  <p className="text-[#4A4A4A] text-sm leading-relaxed mb-4 line-clamp-4">
                    {post.caption}
                  </p>

                  {/* Engagement */}
                  <div className="flex items-center justify-between text-sm text-[#9E9E9E] mb-4">
                    <div className="flex items-center space-x-4">
                      <div className="flex items-center space-x-1">
                        <SafeIcon icon={FiHeart} className="h-4 w-4" />
                        <span>{post.likes}</span>
                      </div>
                      <div className="flex items-center space-x-1">
                        <SafeIcon icon={FiMessageCircle} className="h-4 w-4" />
                        <span>{post.comments}</span>
                      </div>
                    </div>
                    <span>{post.date}</span>
                  </div>

                  {/* Actions */}
                  <div className="flex items-center justify-between pt-4 border-t border-gray-100">
                    <a 
                      href="https://www.instagram.com/samsnutrihub" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-[#2E7D32] font-medium hover:underline text-sm"
                    >
                      View on Instagram
                    </a>
                    <button className="text-[#9E9E9E] hover:text-[#2E7D32] transition-colors duration-200">
                      <SafeIcon icon={FiShare} className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              </motion.div>
            ))}
          </motion.div>

          {/* Load More */}
          <motion.div 
            {...fadeInUp}
            className="text-center mt-12"
          >
            <a 
              href="https://www.instagram.com/samsnutrihub" 
              target="_blank" 
              rel="noopener noreferrer"
              className="inline-flex items-center space-x-2 bg-[#2E7D32] text-white px-8 py-4 rounded-lg font-semibold hover:bg-[#205C25] transition-colors duration-200"
            >
              <SafeIcon icon={FiInstagram} className="h-5 w-5" />
              <span>View More on Instagram</span>
            </a>
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-[#205C25] py-20">
        <div className="max-w-4xl mx-auto text-center px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-3xl md:text-4xl font-bold text-white font-poppins mb-6">
              Ready to Share Your Success Story?
            </h2>
            <p className="text-xl text-gray-200 mb-8">
              Join our community of transformation warriors and let your journey inspire others.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a 
                href="/body-analysis"
                className="bg-white text-[#205C25] px-8 py-4 rounded-lg font-semibold hover:bg-gray-100 transition-colors duration-200"
              >
                Start Your Journey
              </a>
              <a 
                href="https://www.instagram.com/samsnutrihub" 
                target="_blank" 
                rel="noopener noreferrer"
                className="border-2 border-white text-white px-8 py-4 rounded-lg font-semibold hover:bg-white hover:text-[#205C25] transition-colors duration-200 flex items-center justify-center space-x-2"
              >
                <SafeIcon icon={FiInstagram} className="h-5 w-5" />
                <span>Follow Us</span>
              </a>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default InstagramPosts;